import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAppDispatch } from '../../hooks/useAppDispatch'
import { setAccessToken, setUser } from '../../store/slices/authSlice'
import { apiSlice } from '../../store/api/apiSlice'
import { api } from '../../lib/apiClient'
import { ENDPOINTS } from '../../lib/endpoints'
import { REDIRECT_DELAY_MS, STORAGE_KEYS } from '../../lib/config'

export default function AuthCallback() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [status, setStatus] = useState<'processing' | 'error'>('processing')
  const [message, setMessage] = useState('Signing you in...')

  const error = searchParams.get('error')
  const code = searchParams.get('code')

  useEffect(() => {
    if (error || !code) {
      setStatus('error')
      setMessage('Login failed. Redirecting...')
      setTimeout(() => void navigate('/'), REDIRECT_DELAY_MS)
      return
    }

    const returnTo = searchParams.get('returnTo')

    // Exchange the one-time code (set by the backend's OAuth callback redirect)
    // for the real access/refresh tokens — see auth.routes.ts /auth/exchange-code.
    // The code is single-use and expires in 60s, so this must happen immediately.
    api.post<{ accessToken: string; refreshToken: string; isNew: boolean }>('auth', ENDPOINTS.auth.exchangeCode, { code })
      .then((tokens) => {
        // Store refresh token in localStorage for cross-domain deployments
        // where third-party cookies are blocked by the browser
        if (tokens.refreshToken) {
          try { localStorage.setItem(STORAGE_KEYS.refreshToken, tokens.refreshToken) } catch {}
        }

        dispatch(setAccessToken(tokens.accessToken))

        // Do NOT set user from JWT here — avatarUrl and displayName are not in the
        // JWT payload. /auth/me below fetches the complete profile from DB.
        // Setting a partial user here would flash null avatarUrl before /auth/me responds.

        // Fetch full profile (displayName, avatarUrl) — but don't rotate the refresh token
        // App.tsx's timer already manages rotation; a second simultaneous rotation here
        // causes "invalid refresh token" race conditions and signs the user out.
        setMessage('Loading your profile...')
        return api.get<{ user: { id: string; email: string; displayName: string; avatarUrl: string | null; roles: string[] } }>('auth', ENDPOINTS.auth.me, tokens.accessToken)
          .then((data) => {
            dispatch(setUser(data.user))
            dispatch(apiSlice.util.invalidateTags(['Discussion']))
          })
          .catch(() => {})
          .finally(() => {
            sessionStorage.removeItem(STORAGE_KEYS.returnTo)
            void navigate(tokens.isNew ? '/welcome' : (returnTo ?? '/'), { replace: true })
          })
      })
      .catch(() => {
        setStatus('error')
        setMessage('Login failed. Redirecting...')
        setTimeout(() => void navigate('/'), REDIRECT_DELAY_MS)
      })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      background: 'var(--bg-base)',
      color: 'var(--text-primary)',
      gap: '1rem',
      fontFamily: 'Inter, sans-serif',
    }}>
      {status === 'processing' ? (
        <>
          <div style={{
            width: '44px',
            height: '44px',
            border: '3px solid var(--accent-primary-dim, rgba(124,58,237,0.3))',
            borderTopColor: 'var(--accent-primary, #7c3aed)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
          <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Ekkam AI</div>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>{message}</div>
        </>
      ) : (
        <>
          <div style={{ fontSize: '2rem' }}>⚠️</div>
          <div style={{ color: 'var(--status-error)', fontWeight: 600 }}>Login failed</div>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Redirecting to home...</div>
        </>
      )}
    </div>
  )
}
